import React, {Fragment} from "react";
import {postData} from "../../../utils/request";
import Button from "../Button";
import classNames from "classnames";
import {isTrue} from "../../../utils/other";
import {loadComponentById} from "../../../utils/components";
import Modules from "./Modules";
import ContentLoader from "../../layout/ContentLoader";
import NodeEditor from "./NodeEditor";
import AttributeEditor from "./AttributeEditor";

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const JSScript = document.createElement('script');
    JSScript.type = 'text/javascript';
    JSScript.src = 'https://cdn.jsdelivr.net/npm/srcdoc-polyfill@1.0.0/srcdoc-polyfill.min.js';
    document.head.appendChild(JSScript);
}

export default class MjmlField extends React.Component {

    state = {
        isLoading: true,
        html: "",
        attributes: {},
        parts: [],
        fullScreen: false,
    };

    constructor(props) {
        super(props);
        this.calculateHtml();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.value + "" !== "" + prevProps.value) {
            this.calculateHtml();
        }
    }

    calculateHtml = async () => {
        this.abort && this.abort();
        let abort = false;
        this.abort = () => abort = true;
        if (this.props.value) {
            const parse = await this.parse();
            this.mjml = postData({
                action: "Newsletter.render",
                engine: "mjml",
                mjml: parse.mjml
            });
            this.mjml.then((res) => {
                if (abort) return;
                this.setState({
                    html: res.html,
                    parts: parse.parts,
                    attributes: parse.attributes,
                });
            });
            this.mjml.catch((e) => {
                if (abort) return;
                console.log(e);
                this.setState({
                    html: '',
                    parts: [],
                    attributes: [],
                });
            })
        } else {
            if (abort) return;
            this.setState({
                html: '',
                parts: [],
                attributes: [],
            });
        }
    }

    parse = async () => {
        const parts = [];
        const attributes = {};
        const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');

        //console.log(dom.documentElement.outerHTML);

        await Promise.all(Object.keys(AttributeEditor.attributes).map(async (attributeName) => {
            const attributeNodes = [];
            dom.querySelectorAll("mj-head mj-attributes " + attributeName).forEach((attribute) => attributeNodes.push(attribute));
            attributes[attributeName] = await AttributeEditor.attributes[attributeName].parse(attributeNodes);
        }));

        const sections = [];
        dom.querySelectorAll("mj-include[id]").forEach((section) => {
            sections.push(section);
            parts.push({
                component: "MjmlPart",
                id: section.getAttribute("id"),
                variables: {},
                fixed: isTrue(section.getAttribute("data-fixed")),
                head: section.parentNode.nodeName === "mj-head",
                excluded: isTrue(section.getAttribute("data-excluded"))
            });
        });
        this.setState({isLoading: true});
        for (const [index, section] of sections.entries()) {
            const component = await loadComponentById("MjmlPart", section.getAttribute("id"));
            const mjmlSection = new DOMParser().parseFromString("<mjml>" + component.mjml + "</mjml>", 'text/xml');
            const dataNodes = [];
            mjmlSection.querySelectorAll(":scope [data-id]").forEach((dataNode) => {
                dataNodes.push(dataNode);
            });
            for (const dataNode of dataNodes) {
                const variableName = dataNode.getAttribute("data-id");
                const type = dataNode.nodeName;
                const variables = section.querySelectorAll(":scope > data-variable[id='" + variableName + "']");
                parts[index].variables[variableName] = {type: dataNode.nodeName};
                parts[index].variables[variableName].data = await NodeEditor.nodes[type].parse(dataNode, variables);
                parts[index].variables[variableName].data = parts[index].variables[variableName].data || {};
                dataNode.removeAttribute("data-id");
            }
            let wrapper;
            if (section.parentNode.nodeName === "mj-body") {
                wrapper = dom.createElement("mj-wrapper");
                const domId = "section_" + new Date().getTime();
                wrapper.setAttribute("css-class", domId);
                parts[index].domId = domId;
            } else {
                wrapper = dom.createDocumentFragment();
            }
            mjmlSection.querySelector("mjml").childNodes.forEach((child) => {
                wrapper.appendChild(child);
            });
            section.parentNode.replaceChild(wrapper, section);
        }

        const images = [];
        dom.querySelectorAll("mj-body mj-image").forEach((image) => images.push(image));
        await Promise.all(images.map(async (image) => {
            const id = image.getAttribute("id");
            if (id) {
                const component = await loadComponentById("figure", id);
                image.setAttribute("src", component.url);
                image.removeAttribute("id");
            } else {
                if (!image.getAttribute("src")) {
                    image.parentNode.removeChild(image);
                }
            }
        }));

        const links = [];
        dom.querySelectorAll("mj-body [href]").forEach((link) => links.push(link));
        await Promise.all(links.map(async (link) => {
            const href = link.getAttribute("href");
            if (!isNaN(href)) {
                try {
                    const component = await loadComponentById("url", href);
                    link.setAttribute("href", component.url);
                } catch (e) {
                    console.log(e);
                }
            }
        }));

        const components = [];
        dom.querySelectorAll("mj-body component").forEach((component) => components.push(component));
        await Promise.all(components.map(async (component) => {
            const type = component.getAttribute("type");
            if (type !== undefined) {
                if (type === "link") {
                    const a = dom.createElement("a");
                    a.setAttribute("href", "javascript:void(0);");
                    a.innerHTML = component.innerHTML;
                    component.parentNode.replaceChild(a, component);
                }
            }
        }));
        this.setState({isLoading: false});

        //console.log(dom.documentElement.outerHTML);

        return {mjml: dom.documentElement.outerHTML, parts, attributes};
    }

    isFullScreen = (value) => {
        return value === 'on' || isTrue(value);
    }

    saveChanges = (value) => {
        if (this.props.saveChanges) {
            this.props.saveChanges(this.props.name, value);
        }
    }

    onAttributeChanged = (attributeName, attribute) => {
        const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');
        const attributeNodes = [];
        dom.querySelectorAll("mjml mj-head mj-attributes " + attributeName).forEach((attribute) => attributeNodes.push(attribute));
        AttributeEditor.attributes[attributeName].onAttributeChanged(attributeNodes, attributeName, attribute);
        this.saveChanges(dom.documentElement.outerHTML);
    }

    onSortEnd = async ({oldIndex, newIndex}) => {
        if (oldIndex !== newIndex) {
            const oldPosition = oldIndex;
            const distance = Math.abs(oldIndex - newIndex);
            const newPosition = (oldIndex - newIndex) > 0 ? oldPosition - distance : oldPosition + distance;
            let direction = 0;
            if (oldIndex > newIndex) {
                direction = 1;
            }
            const newSort = [];

            const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');
            const sections = [];
            dom.querySelectorAll("mjml mj-include[id]").forEach((section) => sections.push(section));

            sections.forEach((item, index) => {
                if (index < newPosition && oldPosition !== index) {
                    //console.log('lower');
                    //console.log(item.getAttribute("id"));
                    newSort.push(item.cloneNode(true));
                } else if (index === newPosition) {
                    //console.log('equal');
                    //console.log(item.getAttribute("id"));
                    if (direction > 0) {
                        newSort.push(sections[oldPosition].cloneNode(true));
                        newSort.push(item.cloneNode(true));
                    } else {
                        newSort.push(item.cloneNode(true));
                        newSort.push(sections[oldPosition].cloneNode(true));
                    }
                } else if (index > newPosition && oldPosition !== index) {
                    //console.log('higher');
                    //console.log(item.getAttribute("id"));
                    newSort.push(item.cloneNode(true));
                }
            });
            //console.log(newSort.map((include) => include.getAttribute("id")));

            dom.querySelectorAll("mjml mj-include[id]").forEach((section, index) => section.parentNode.replaceChild(newSort[index], section));

            //console.log(dom.documentElement.outerHTML);
            this.saveChanges(dom.documentElement.outerHTML);
        }
    }

    onPartRemove = (removeIndex) => {
        const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');
        let removed = false;
        dom.querySelectorAll("mjml mj-include[id]").forEach((section, index) => {
            if (removed) return;
            if (index === removeIndex) {
                section.parentNode.removeChild(section);
                removed = true;
            }
        });
        this.saveChanges(dom.documentElement.outerHTML);
    }

    onPartInsert = (partIndex, position, partId) => {
        const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');

        const part = dom.createElement("mj-include");
        part.setAttribute("id", partId);

        dom.querySelectorAll("mjml mj-include[id]").forEach((section, index) => {
            if (index === partIndex) {
                if(position < 0) {
                    section.parentNode.insertBefore(part, section);
                } else if (position > 0) {
                    // https://stackoverflow.com/questions/4793604/how-to-insert-an-element-after-another-element-in-javascript-without-using-a-lib
                    // insertAfter
                    section.parentNode.insertBefore(part, section.nextSibling);
                }
            }
        });
        this.saveChanges(dom.documentElement.outerHTML);
    }

    onVariableChanged = (partIndex, variableName, variable, document = null) => {
        const dom = document || (new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml'));
        dom.querySelectorAll("mjml mj-include[id]").forEach((section, index) => {
            if (index === partIndex) {
                section.querySelectorAll(":scope > data-variable[id='" + variableName + "']").forEach((variableNode) => {
                    variableNode.parentNode.removeChild(variableNode);
                });
                const variableNode = dom.createElement("data-variable");
                variableNode.setAttribute("id", variableName);
                section.appendChild(variableNode);
                NodeEditor.nodes[this.state.parts[partIndex].variables[variableName].type].onVariableChanged(variableNode, variableName, variable);
            }
        });
        !document && this.saveChanges(dom.documentElement.outerHTML);
    }

    onVariablesChanged = (partIndex, variables) => {
        const dom = new DOMParser().parseFromString(this.props.value || "<mjml></mjml>", 'text/xml');
        Object.keys(variables).map((variableName) => {
            const variable = variables[variableName];
            this.onVariableChanged(partIndex, variableName, variable, dom);
        });
        this.saveChanges(dom.documentElement.outerHTML);
    }

    onFocusSection = (partIndex) => {
        if (this.iframe) {
            this.onFocusSectionTimeout && clearTimeout(this.onFocusSectionTimeout);
            const document = this.iframe.contentWindow.document;
            //const window = this.iframe.contentWindow;
            let focusStyle = document.head.querySelector("style#focusStyle");
            if (!focusStyle) {
                focusStyle = document.createElement("style");
                focusStyle.id = "focusStyle";
                focusStyle.rel = "stylesheet";
                focusStyle.type = "text/css";
                document.head.appendChild(focusStyle);
            }
            if (this.state.parts[partIndex].domId) {
                document.querySelector("." + this.state.parts[partIndex].domId).scrollIntoView();
                focusStyle.innerHTML = `
                .${this.state.parts[partIndex].domId} {
  box-shadow: 0 0 0 0 rgba(255, 82, 82, 1);
  animation: pulse-red 2s;
}

@keyframes pulse-red {
  0% {
    //transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.9);
  }

  70% {
    //transform: scale(1);
    box-shadow: 0 0 0 5px rgba(255, 82, 82, 0);
  }

  100% {
    //transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}
                `;
            } else {
                focusStyle.innerHTML = '';
            }
            this.onFocusSectionTimeout = setTimeout(() => {
                focusStyle.innerHTML = '';
            }, 1500);
        }
    }

    attachDomEvents = () => {
        //const document = this.iframe.contentWindow.document;
    }

    onSelectSection = () => {

    }

    renderButton = (btn, key) => {
        if (btn instanceof Function) {
            return btn({
                key: key,
                value: this.props.value,
                disabled: this.props.disabled || this.state.isLoading
            });
        }
    }

    render() {
        let style = {};
        if (this.props.label === false) {
            style.display = "none";
        }

        let content;
        if (this.state.isLoading) {
            content = <div className="loader-holder"><ContentLoader />{/*<div className="loader"><div className="load" style={{width: (100 - (this.state.isLoading / this.state.isLoadings * 100)) + '%'}} /></div>*/}</div>;
        } else {
            content = <iframe ref={(iframe) => {this.iframe = iframe; this.attachDomEvents()}} srcDoc={this.state.html} />;
        }
        return (
            <div className={classNames("mjml-editor", {"full-screen": this.isFullScreen(this.state.fullScreen)})}>
                <label htmlFor={this.props.name} className="control-label" style={style}>{this.props.label} {this.props.required && <span className="invalid-feedback">*</span>}</label>
                <div className="toolbar">
                    {(this.props.toolbar || []).map((btn, key) => this.renderButton(btn, key))}
                </div>
                <div className="editing">
                    <div className="build">
                        <Modules
                            disabled={this.props.disabled || this.state.isLoading}
                            attributes={this.state.attributes}
                            parts={this.state.parts}
                            component={this.props.component}
                            componentId={this.props.componentId}
                            className="parts"
                            onSelectSection={this.onSelectSection}
                            onFocusSection={this.onFocusSection}
                            onAttributeChanged={this.onAttributeChanged}
                            onPartInsert={this.onPartInsert}
                            onVariableChanged={this.onVariableChanged}
                            onVariablesChanged={this.onVariablesChanged}
                            onSortEnd={this.onSortEnd}
                            onPartRemove={this.onPartRemove}
                        />
                    </div>
                </div>
                {content}
                <Button
                    block={true}
                    tooltip={!this.state.fullScreen ? "Szerkesztés" : "Bezárás"}
                    className="full-screen-button"
                    color="default"
                    onClick={() => this.setState({fullScreen: !this.state.fullScreen})}
                    icon={{lib: "fas", name: !this.state.fullScreen ? "pencil-alt" : "times"}}
                />
            </div>
        );
    }
}